import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  Button,
  Checkbox,
  getCompactSelectStyles,
  Input,
  Select,
  Textarea,
} from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import {
  TagGroup,
  TagGroupType,
  TagSelectionMode,
  TagSpec,
} from '@/entities/analyticsModel';

import { analyticsModelMessages } from '../../config/i18n';

import styles from './TagGroupEditor.module.scss';

type TagGroupEditorProps = {
  id: string;
  group: TagGroup;
  index: number;
  anchorId?: string;
  canEdit?: boolean;
  isEditing?: boolean;
  isInteractionDisabled?: boolean;
  className?: string;
  onChange: (updated: TagGroup) => void;
  onRemove: () => void;
  onEdit?: () => void;
  onEditCode?: () => void;
  onFocus?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
};

type SortableTagEditorRowProps = {
  id: string;
  groupIndex: number;
  tagIndex: number;
  tag: TagSpec;
  isInputType: boolean;
  onUpdate: (tagIndex: number, updated: TagSpec) => void;
  onRemove: (tagIndex: number) => void;
};

const SortableTagEditorRow: FC<SortableTagEditorRowProps> = (props) => {
  const { id, groupIndex, tagIndex, tag, isInputType, onUpdate, onRemove } =
    props;

  const { formatMessage } = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      data-component="tag-group-editor-row"
      className={cn(styles.tagRow, isDragging && styles.tagRowDragging)}
    >
      <div className={styles.tagRowHeader}>
        <span
          className={cn(styles.tagNumber, styles.tagNumberDraggable)}
          {...attributes}
          {...listeners}
        >
          {groupIndex + 1}.{tagIndex + 1}
        </span>
        <div className={styles.tagFields}>
          <Input
            name={`tagName-${groupIndex}-${tagIndex}`}
            value={tag.name}
            onChange={(e) =>
              onUpdate(tagIndex, { ...tag, name: e.target.value })
            }
            placeholder={formatMessage(analyticsModelMessages.tagName)}
            className={styles.tagInput}
          />
        </div>
        <div className={styles.tagActions}>
          {!isInputType && (
            <label
              className={styles.checkboxLabel}
              title={formatMessage(
                analyticsModelMessages.tagExplainDescription,
              )}
            >
              <Checkbox
                checked={tag.explain ?? false}
                onChange={() =>
                  onUpdate(tagIndex, {
                    ...tag,
                    explain: !tag.explain,
                  })
                }
              />
              {formatMessage(analyticsModelMessages.tagExplain)}
            </label>
          )}
          <Button
            variant="tertiary"
            size="tiny"
            icon="delete"
            circle
            className={styles.deleteButton}
            onClick={() => onRemove(tagIndex)}
            title={formatMessage(analyticsModelMessages.removeTag)}
          />
        </div>
      </div>
      {!isInputType && (
        <div className={styles.tagInstructionsRow}>
          <Textarea
            value={tag.instructions ?? ''}
            onChange={(e) =>
              onUpdate(tagIndex, {
                ...tag,
                instructions: e.target.value,
              })
            }
            placeholder={formatMessage(
              analyticsModelMessages.tagInstructionsPlaceholder,
            )}
            rows={1}
            className={styles.instructionsField}
          />
        </div>
      )}
    </div>
  );
};

export const TagGroupEditor: FC<TagGroupEditorProps> = (props) => {
  const {
    id,
    anchorId,
    group,
    index,
    canEdit,
    isEditing,
    isInteractionDisabled,
    className,
    onChange,
    onEdit,
    onEditCode,
    onFocus,
    onSave,
    onCancel,
    onRemove,
  } = props;

  const { formatMessage } = useIntl();
  const tagDndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const isDragDisabled = Boolean(
    !canEdit || isEditing || isInteractionDisabled,
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: isDragDisabled,
  });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  const selectionModeOptions = useMemo(
    () => [
      {
        label: formatMessage(analyticsModelMessages.selectionSingle),
        value: 'single',
      },
      {
        label: formatMessage(analyticsModelMessages.selectionMultiple),
        value: 'multiple',
      },
    ],
    [formatMessage],
  );

  const isInputType = group.type === 'input';

  const handleFieldChange = useCallback(
    (
      field: keyof TagGroup,
      value: string | boolean | TagSelectionMode | TagGroupType,
    ) => {
      onChange({ ...group, [field]: value });
    },
    [group, onChange],
  );

  const handleSelectionModeChange = useCallback(
    (value: React.ChangeEvent<Element> | ValueType) => {
      if (typeof value === 'string') {
        handleFieldChange('selection_mode', value as TagSelectionMode);
      }
    },
    [handleFieldChange],
  );

  const handleAddTag = useCallback(() => {
    const newTag: TagSpec = {
      code: '',
      name: '',
      instructions: '',
      explain: false,
    };
    onChange({ ...group, tags: [...group.tags, newTag] });
  }, [group, onChange]);

  const handleUpdateTag = useCallback(
    (tagIndex: number, updated: TagSpec) => {
      const newTags = [...group.tags];
      newTags[tagIndex] = updated;
      onChange({ ...group, tags: newTags });
    },
    [group, onChange],
  );

  const handleRemoveTag = useCallback(
    (tagIndex: number) => {
      onChange({ ...group, tags: group.tags.filter((_, i) => i !== tagIndex) });
    },
    [group, onChange],
  );

  const tagIds = useMemo(
    () => group.tags.map((_, tagIndex) => `${id}-tag-${tagIndex}`),
    [group.tags, id],
  );

  const handleTagDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const activeIndex = tagIds.indexOf(String(active.id));
      const overIndex = tagIds.indexOf(String(over.id));

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      onChange({
        ...group,
        tags: arrayMove(group.tags, activeIndex, overIndex),
      });
    },
    [group, onChange, tagIds],
  );

  const getTypeLabel = (type: TagGroupType | undefined): string =>
    type === 'input'
      ? formatMessage(analyticsModelMessages.groupTypeInput)
      : formatMessage(analyticsModelMessages.groupTypeAi);

  const getSelectionLabel = (mode: TagSelectionMode): string =>
    mode === 'multiple'
      ? formatMessage(analyticsModelMessages.selectionMultiple)
      : formatMessage(analyticsModelMessages.selectionSingle);

  if (!isEditing) {
    return (
      <div
        id={anchorId}
        ref={setNodeRef}
        style={sortableStyle}
        className={cn(
          styles.container,
          isDragging && styles.containerDragging,
          isInteractionDisabled && styles.containerDisabled,
          className,
        )}
        onClick={onFocus}
        onFocus={onFocus}
      >
        <div className={styles.header}>
          <span
            className={cn(
              styles.numberLabel,
              !isDragDisabled && styles.numberLabelDraggable,
            )}
            {...(isDragDisabled ? {} : attributes)}
            {...(isDragDisabled ? {} : listeners)}
          >
            {index + 1}
          </span>
          <div className={styles.headerFields}>
            <div className={styles.titleCluster}>
              <div className={styles.readOnlyTitleRow}>
                <span className={styles.readOnlyName}>{group.name}</span>
              </div>
              <span className={styles.readOnlyMeta}>
                {[
                  getTypeLabel(group.type),
                  group.required
                    ? formatMessage(analyticsModelMessages.required)
                    : formatMessage(analyticsModelMessages.optional),
                  getSelectionLabel(group.selection_mode),
                ].join(' \u00B7 ')}
              </span>
            </div>
          </div>
          {canEdit && (
            <div className={styles.actions}>
              <Button
                variant="tertiary"
                size="tiny"
                icon="pen"
                circle
                onClick={onEdit}
                disabled={isInteractionDisabled}
                title={formatMessage(analyticsModelMessages.edit)}
              />
              <Button
                variant="tertiary"
                size="tiny"
                icon="delete"
                circle
                className={styles.deleteButton}
                onClick={onRemove}
                disabled={isInteractionDisabled}
                title={formatMessage(analyticsModelMessages.delete)}
              />
            </div>
          )}
        </div>

        {!isInputType && group.instructions && (
          <p className={styles.readOnlyText}>{group.instructions}</p>
        )}

        <div className={styles.tagsSection}>
          {group.tags.map((tag, tagIndex) => (
            <div key={`${tag.code}-${tagIndex}`} className={styles.tagRow}>
              <div className={styles.tagRowHeader}>
                <span className={styles.tagNumber}>
                  {index + 1}.{tagIndex + 1}
                </span>
                <div className={styles.tagFields}>
                  <div className={styles.readOnlyTagRow}>
                    <span className={styles.readOnlyTagName}>{tag.name}</span>
                  </div>
                  {tag.explain && !isInputType && (
                    <span
                      className={styles.explainBadge}
                      title={formatMessage(
                        analyticsModelMessages.tagExplainDescription,
                      )}
                    >
                      {formatMessage(analyticsModelMessages.tagExplain)}
                    </span>
                  )}
                </div>
              </div>
              {!isInputType && tag.instructions && (
                <div className={styles.tagInstructionsRow}>
                  <p className={styles.readOnlyText}>{tag.instructions}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Edit view
  return (
    <div
      id={anchorId}
      ref={setNodeRef}
      style={sortableStyle}
      className={cn(
        styles.container,
        styles.containerEditing,
        isDragging && styles.containerDragging,
        className,
      )}
      onClick={onFocus}
      onFocus={onFocus}
    >
      <div className={styles.header}>
        <span className={styles.numberLabel}>{index + 1}</span>
        <div className={styles.editorBody}>
          <div className={styles.editorMainRow}>
            <div className={styles.headerFields}>
              <Input
                name={`groupName-${index}`}
                value={group.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder={formatMessage(analyticsModelMessages.groupName)}
                className={styles.nameInput}
              />
            </div>
            <div className={styles.primaryActions}>
              <Button variant="primary" size="small" onClick={onSave}>
                {formatMessage(analyticsModelMessages.save)}
              </Button>
              <Button variant="tertiary" size="small" onClick={onCancel}>
                {formatMessage(analyticsModelMessages.cancel)}
              </Button>
              {onEditCode && (
                <Button variant="tertiary" size="small" onClick={onEditCode}>
                  {formatMessage(analyticsModelMessages.advanced)}
                </Button>
              )}
            </div>
          </div>

          <div className={styles.editorControlsRow}>
            <div className={styles.secondaryActions}>
              <div className={styles.typeToggle}>
                <button
                  type="button"
                  className={cn(
                    styles.typeButton,
                    !isInputType && styles.typeButtonActive,
                  )}
                  onClick={() => handleFieldChange('type', 'ai')}
                >
                  {formatMessage(analyticsModelMessages.groupTypeAi)}
                </button>
                <button
                  type="button"
                  className={cn(
                    styles.typeButton,
                    isInputType && styles.typeButtonActive,
                  )}
                  onClick={() => handleFieldChange('type', 'input')}
                >
                  {formatMessage(analyticsModelMessages.groupTypeInput)}
                </button>
              </div>
              <Select
                options={selectionModeOptions}
                value={group.selection_mode}
                onChange={handleSelectionModeChange}
                className={styles.controlSelect}
                styles={getCompactSelectStyles()}
              />
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={group.required}
                  onChange={() =>
                    handleFieldChange('required', !group.required)
                  }
                />
                {formatMessage(analyticsModelMessages.required)}
              </label>
            </div>
          </div>
        </div>
      </div>

      {!isInputType && (
        <div className={styles.instructionsRow}>
          <Textarea
            value={group.instructions ?? ''}
            onChange={(e) => handleFieldChange('instructions', e.target.value)}
            placeholder={formatMessage(
              analyticsModelMessages.groupInstructionsPlaceholder,
            )}
            rows={2}
            className={styles.instructionsField}
          />
        </div>
      )}

      <div className={styles.tagsSection}>
        <DndContext
          sensors={tagDndSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleTagDragEnd}
        >
          <SortableContext
            items={tagIds}
            strategy={verticalListSortingStrategy}
          >
            {group.tags.map((tag, tagIndex) => (
              <SortableTagEditorRow
                key={tagIds[tagIndex]}
                id={tagIds[tagIndex]}
                groupIndex={index}
                tagIndex={tagIndex}
                tag={tag}
                isInputType={isInputType}
                onUpdate={handleUpdateTag}
                onRemove={handleRemoveTag}
              />
            ))}
          </SortableContext>
        </DndContext>
        <Button
          variant="secondary"
          size="small"
          onClick={handleAddTag}
          className={styles.addTagButton}
        >
          {formatMessage(analyticsModelMessages.addTag)}
        </Button>
      </div>
    </div>
  );
};
